import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Fingerprint,
  BusinessCenter,
  Security,
  Speed,
  CloudQueue,
  Analytics,
  Timeline,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionChip = motion(Chip);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    try {
      const result = await login(email, password);
      if (result.success) {
        localStorage.setItem('welcomeMessage', `Welcome back! Authentication successful.`);
        setTimeout(() => {
          navigate('/');
        }, 500);
      } else {
        setError(result.message || 'Authentication failed');
        setIsLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'System error occurred');
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'auto',
        background: '#020814',
        py: { xs: 4, sm: 6, md: 0 },
      }}
    >
      {/* SEO Meta Tags */}
      <title>ATMS - Advanced Employee Attendance Management System | Smart Biometric Login</title>
      <meta name="description" content="Enterprise-grade Employee Attendance Management System with biometric verification, real-time tracking, AI-powered analytics, and secure cloud storage. Streamline workforce management with ATMS." />
      <meta name="keywords" content="employee attendance, attendance management system, biometric attendance, workforce management, smart attendance, ATMS, employee tracking" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes" />
      
      {/* Background Gradient Overlays */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 30% 50%, rgba(0, 140, 255, 0.25), transparent 30%),
            radial-gradient(circle at 80% 50%, rgba(0, 180, 255, 0.18), transparent 30%)
          `,
          filter: 'brightness(0.35)',
        }}
      />

      {/* Blue Light Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(130deg, transparent 25%, rgba(0, 130, 255, 0.12) 40%, transparent 60%)',
          mixBlendMode: 'screen',
        }}
      />

      {/* Floating Particles - Reduced for mobile */}
      {[...Array(isMobile ? 20 : 40)].map((_, i) => (
        <motion.div
          key={i}
          className="particle"
          style={{
            width: Math.random() * (isMobile ? 3 : 5) + 1,
            height: Math.random() * (isMobile ? 3 : 5) + 1,
            background: `rgba(0, 140, 255, ${Math.random() * 0.5})`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -40, 0],
            x: [0, Math.random() * 30 - 15, 0],
            opacity: [0, 0.6, 0],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: Math.random() * 5 + 4,
            repeat: Infinity,
            delay: Math.random() * 5,
          }}
        />
      ))}

      {/* SEO Content Sidebar - Left (Hidden on mobile/tablet) */}
      {!isMobile && !isTablet && (
        <MotionBox
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          sx={{
            position: 'fixed',
            left: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 280,
            display: { xs: 'none', lg: 'block' },
            zIndex: 15,
          }}
        >
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: '#00F2FE', mb: 2, fontWeight: 700 }}>
              Smart Attendance System
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Fingerprint sx={{ color: '#00F2FE', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#c8d2df' }}>
                  Biometric Verification Ready
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Timeline sx={{ color: '#00F2FE', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#c8d2df' }}>
                  Real-time Attendance Tracking
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Analytics sx={{ color: '#00F2FE', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#c8d2df' }}>
                  AI-Powered Analytics Dashboard
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CloudQueue sx={{ color: '#00F2FE', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#c8d2df' }}>
                  Cloud-Based Secure Storage
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            <Typography variant="caption" sx={{ color: '#9ca8bb', display: 'block', textAlign: 'center' }}>
              Trusted by 5000+ Enterprises
            </Typography>
          </Box>
        </MotionBox>
      )}

      {/* SEO Content Sidebar - Right (Hidden on mobile/tablet) */}
      {!isMobile && !isTablet && (
        <MotionBox
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          sx={{
            position: 'fixed',
            right: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 260,
            display: { xs: 'none', lg: 'block' },
            zIndex: 15,
          }}
        >
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              padding: '24px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="h6" sx={{ color: '#00F2FE', mb: 2, fontWeight: 700 }}>
              Key Benefits
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ color: '#00F2FE', fontWeight: 600 }}>
                  99.9% Uptime
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca8bb' }}>
                  Enterprise-grade reliability
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#00F2FE', fontWeight: 600 }}>
                  GDPR Compliant
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca8bb' }}>
                  Data protection & privacy
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#00F2FE', fontWeight: 600 }}>
                  24/7 Support
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca8bb' }}>
                  Dedicated assistance team
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: '#00F2FE', fontWeight: 600 }}>
                  Mobile App
                </Typography>
                <Typography variant="caption" sx={{ color: '#9ca8bb' }}>
                  iOS & Android compatible
                </Typography>
              </Box>
            </Stack>
          </Box>
        </MotionBox>
      )}

      {/* Wrapper */}
      <Box sx={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '560px', mx: 'auto' }}>
        {/* Left Accent - Hidden on mobile */}
        {!isMobile && (
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          className="login-accent-left"
          />
        )}

        {/* Main Login Card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100, damping: 20, duration: 0.8 }}
          className="login-main-card"
          style={{
            '--login-card-padding': isMobile ? '40px 24px' : '60px 50px',
            clipPath: isMobile ? 'none' : 'polygon(8% 0, 78% 0, 100% 18%, 100% 100%, 20% 100%, 0 82%, 0 8%)',
            borderRadius: isMobile ? '32px' : '0',
          }}
        >
          {/* Gloss Reflection - Hidden on mobile */}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                width: '250%',
                height: '250%',
                top: '-70%',
                left: '-90%',
                transform: 'rotate(-25deg)',
                background: 'linear-gradient(90deg, transparent, rgba(0, 140, 255, 0.25), rgba(255, 255, 255, 0.08), transparent)',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Logo */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: isMobile ? '40px' : '70px' }}>
              <BusinessCenter sx={{ color: '#00F2FE', fontSize: isMobile ? 36 : 48 }} />
              <Typography
                sx={{
                  color: 'white',
                  fontSize: isMobile ? '32px' : '42px',
                  fontWeight: 700,
                  letterSpacing: '2px',
                  background: 'linear-gradient(135deg, #fff, #00F2FE)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                ATMS
              </Typography>
            </Box>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Typography
              sx={{
                color: 'white',
                fontSize: isMobile ? '36px' : '48px',
                fontWeight: 700,
                mb: '10px',
                textAlign: 'center',
              }}
            >
              Welcome Back
            </Typography>
          </motion.div>

          {/* Subtitle */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Typography
              sx={{
                color: '#9ca8bb',
                fontSize: isMobile ? '12px' : '13px',
                lineHeight: 1.6,
                textAlign: 'center',
                mb: '25px',
                px: isMobile ? 2 : 0,
              }}
            >
              Access the Advanced Employee Attendance Management System. Track work hours, manage leaves, and view analytics in real-time.
            </Typography>
          </motion.div>

          {/* Features Chips - Scrollable on mobile */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.65 }}
            style={{ 
              '--features-margin-bottom': '25px',
              '--features-padding': isMobile ? '8px 0' : '0',
            }}
            className="login-features-chips"
          >
            <MotionChip
              label="Biometric Ready"
              size="small"
              icon={<Fingerprint sx={{ fontSize: 14 }} />}
              sx={{
                background: 'rgba(0, 140, 255, 0.15)',
                color: '#00F2FE',
                border: '1px solid rgba(0, 140, 255, 0.3)',
                backdropFilter: 'blur(5px)',
                flexShrink: 0,
              }}
            />
            <MotionChip
              label="Real-time Sync"
              size="small"
              icon={<Speed sx={{ fontSize: 14 }} />}
              sx={{
                background: 'rgba(0, 140, 255, 0.15)',
                color: '#00F2FE',
                border: '1px solid rgba(0, 140, 255, 0.3)',
                backdropFilter: 'blur(5px)',
                flexShrink: 0,
              }}
            />
            <MotionChip
              label="AI Analytics"
              size="small"
              icon={<Analytics sx={{ fontSize: 14 }} />}
              sx={{
                background: 'rgba(0, 140, 255, 0.15)',
                color: '#00F2FE',
                border: '1px solid rgba(0, 140, 255, 0.3)',
                backdropFilter: 'blur(5px)',
                flexShrink: 0,
              }}
            />
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="login-divider"
          />

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mb-20"
            >
              <Typography
                sx={{
                  display: 'block',
                  color: '#c8d2df',
                  fontSize: '14px',
                  mb: '10px',
                  fontWeight: 500,
                }}
              >
                Employee Email ID
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="employee@company.com"
                  className="login-input"
                  style={{
                    '--login-input-height': isMobile ? '50px' : '58px',
                    '--login-input-fontsize': isMobile ? '13px' : '14px',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(0, 140, 255, 0.6)';
                    e.target.style.background = 'rgba(0, 140, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.06)';
                  }}
                />
              </Box>
            </motion.div>

            {/* Password Field */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mb-20"
            >
              <Typography
                sx={{
                  display: 'block',
                  color: '#c8d2df',
                  fontSize: '14px',
                  mb: '10px',
                  fontWeight: 500,
                }}
              >
                Secure Password
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your secure password"
                  className="login-input"
                  style={{
                    '--login-input-height': isMobile ? '50px' : '58px',
                    '--login-input-fontsize': isMobile ? '13px' : '14px',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(0, 140, 255, 0.6)';
                    e.target.style.background = 'rgba(0, 140, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                    e.target.style.background = 'rgba(255, 255, 255, 0.06)';
                  }}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#b8c3d6',
                    '&:hover': { color: '#00F2FE' },
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Box>
            </motion.div>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '40px',
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                  color: '#ff6b6b',
                  backdropFilter: 'blur(5px)',
                }}
              >
                {error}
              </Alert>
            )}

            {/* Login Button */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <MotionButton
                type="submit"
                fullWidth
                disabled={isLoading}
                whileHover={{ 
                  y: -3, 
                  boxShadow: '0 0 30px rgba(0, 140, 255, 0.4)',
                  background: 'linear-gradient(135deg, #ffffff, #f0f4ff)',
                }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  height: isMobile ? '54px' : '62px',
                  border: 'none',
                  borderRadius: '40px',
                  marginTop: '15px',
                  background: 'white',
                  color: '#121212',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: '0.3s',
                  textTransform: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    transition: 'left 0.5s',
                  },
                  '&:hover::before': {
                    left: '100%',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: '#121212' }} />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle />
                    Access Dashboard
                  </Box>
                )}
              </MotionButton>
            </motion.div>

            {/* Mobile Features Section */}
            {isMobile && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="mt-30"
              >
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Features
                  </Typography>
                </Divider>
                <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>
                    🔒 Biometric Ready
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>
                    📊 Real-time Analytics
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>
                    ☁️ Cloud Secure
                  </Typography>
                </Stack>
              </motion.div>
            )}

            {/* SEO Footer Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              className="mt-30-center"
            >
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '10px', display: 'block' }}>
                Advanced Employee Attendance Management System • SOC 2 Type II Certified • GDPR Compliant
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '9px', display: 'block', mt: 1 }}>
                © 2024 ATMS - Enterprise Workforce Management Solution
              </Typography>
            </motion.div>
          </form>
        </motion.div>

        {/* Right Accent - Hidden on mobile */}
        {!isMobile && (
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          className="login-accent-right"
          />
        )}
      </Box>
    </Box>
  );
};

export default Login;